import React from 'react';
import { X, Calendar, Clock, BookOpen, User, GraduationCap, CheckCircle2, AlertCircle, FileText, ArrowRight, Trash2 } from 'lucide-react';

const HomeworkDetailModal = ({
  show,
  onClose,
  assignment,
  onOpenSubmissions,
  onDelete,
  canDelete = true
}) => {
  if (!show || !assignment) return null;

  const total = assignment.totalStudents || 0;
  const submitted = assignment.submittedCount || 0;
  const pending = Math.max(0, total - submitted);
  const percentage = total > 0 ? Math.round((submitted / total) * 100) : 0;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const isCompleted = assignment.status === 'Completed';
  const isCancelled = assignment.status === 'Cancelled';

  return (
    <div 
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.6)',
        backdropFilter: 'blur(3px)',
        zIndex: 1150,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div 
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '620px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          border: '1px solid #E2E8F0',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* MODAL HEADER */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #E2E8F0',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',
          backgroundColor: '#F8FAFC'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0F172A' }}>
                {assignment.title}
              </h3>
              {isCompleted ? (
                <span style={{
                  backgroundColor: '#ECFDF5',
                  color: '#059669',
                  border: '1px solid #A7F3D0',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '700',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <CheckCircle2 size={12} /> Completed
                </span>
              ) : isCancelled ? (
                <span style={{
                  backgroundColor: '#F3F4F6',
                  color: '#4B5563',
                  border: '1px solid #E5E7EB',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '700'
                }}>
                  Cancelled
                </span>
              ) : (
                <span style={{
                  backgroundColor: '#EFF6FF',
                  color: '#2563EB',
                  border: '1px solid #BFDBFE',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '700',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <Clock size={12} /> Active
                </span>
              )}
            </div>
            <div style={{ fontSize: '13px', color: '#64748B', marginTop: '4px' }}>
              Academic Year: <strong style={{ color: '#334155' }}>{assignment.academicYear || '2026-2027'}</strong>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#64748B',
              padding: '4px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* MODAL BODY */}
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* METADATA GRID */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            gap: '12px',
            backgroundColor: '#F8FAFC',
            border: '1px solid #E2E8F0',
            borderRadius: '12px',
            padding: '14px'
          }}>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <GraduationCap size={13} color="#2563EB" /> Class & Section
              </div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#0F172A', marginTop: '3px' }}>
                {assignment.className} {assignment.section ? `• Sec ${assignment.section}` : ''}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <BookOpen size={13} color="#059669" /> Subject
              </div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#0F172A', marginTop: '3px' }}>
                {assignment.subject || 'General'}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <User size={13} color="#7C3AED" /> Teacher
              </div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#0F172A', marginTop: '3px' }}>
                {assignment.teacher || 'Assigned Faculty'}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Calendar size={13} color="#D97706" /> Due Date
              </div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#B45309', marginTop: '3px' }}>
                {formatDate(assignment.dueDate)}
              </div>
            </div>
          </div>

          {/* DESCRIPTION / INSTRUCTIONS */}
          <div>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FileText size={14} /> Description / Instructions
            </div>
            <div style={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E2E8F0',
              borderRadius: '10px',
              padding: '14px 16px',
              fontSize: '13.5px',
              lineHeight: '1.6',
              color: '#334155',
              whiteSpace: 'pre-wrap',
              minHeight: '60px'
            }}>
              {assignment.instructions || 'No specific instructions provided.'}
            </div>
          </div>

          {/* SUBMISSION PROGRESS BLOCK */}
          <div style={{
            backgroundColor: '#F0FDF4',
            border: '1px solid #BBF7D0',
            borderRadius: '12px',
            padding: '16px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '12.5px', fontWeight: '700', color: '#166534' }}>
                Submission Summary
              </span>
              <span style={{ fontSize: '12px', fontWeight: '800', color: '#15803D' }}>
                {percentage}% Completed
              </span>
            </div>

            {/* PROGRESS BAR */}
            <div style={{ width: '100%', height: '8px', backgroundColor: '#DCFCE7', borderRadius: '4px', overflow: 'hidden', marginBottom: '12px' }}>
              <div 
                style={{ 
                  width: `${percentage}%`, 
                  height: '100%', 
                  backgroundColor: percentage === 100 ? '#10B981' : '#22C55E', 
                  borderRadius: '4px',
                  transition: 'width 0.4s ease' 
                }} 
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', textAlign: 'center' }}>
              <div style={{ background: '#FFFFFF', borderRadius: '8px', padding: '8px', border: '1px solid #DCFCE7' }}>
                <div style={{ fontSize: '11px', color: '#64748B', fontWeight: '600' }}>Submitted</div>
                <div style={{ fontSize: '16px', fontWeight: '800', color: '#166534' }}>{submitted}</div>
              </div>
              <div style={{ background: '#FFFFFF', borderRadius: '8px', padding: '8px', border: '1px solid #DCFCE7' }}>
                <div style={{ fontSize: '11px', color: '#64748B', fontWeight: '600' }}>Pending</div>
                <div style={{ fontSize: '16px', fontWeight: '800', color: '#DC2626' }}>{pending}</div>
              </div>
              <div style={{ background: '#FFFFFF', borderRadius: '8px', padding: '8px', border: '1px solid #DCFCE7' }}>
                <div style={{ fontSize: '11px', color: '#64748B', fontWeight: '600' }}>Total Students</div>
                <div style={{ fontSize: '16px', fontWeight: '800', color: '#0F172A' }}>{total}</div>
              </div>
            </div>
          </div>

        </div>

        {/* MODAL FOOTER */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #E2E8F0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#F8FAFC',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          {canDelete ? (
            <button
              onClick={() => {
                onDelete(assignment);
                onClose();
              }}
              style={{
                backgroundColor: '#FEF2F2',
                color: '#DC2626',
                border: '1px solid #FEE2E2',
                borderRadius: '8px',
                padding: '8px 14px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Trash2 size={14} /> Delete Homework
            </button>
          ) : <div />}

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={onClose}
              style={{
                backgroundColor: '#FFFFFF',
                color: '#334155',
                border: '1px solid #CBD5E1',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Close
            </button>

            <button
              onClick={() => {
                onClose();
                onOpenSubmissions(assignment);
              }}
              style={{
                backgroundColor: '#2563EB',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 18px',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)'
              }}
            >
              View Submissions <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeworkDetailModal;
